import { useState, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  People,
  PersonAdd,
  AttachMoney,
  CheckCircle,
  Refresh,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  getAllMembersData,
  getActiveMembersData,
  getNewMembersData,
  getRevenueData,
} from "../../api/dashboard";
import "./Dashboard.scss";

// Animated counter component
const AnimatedCounter = ({
  value,
  duration = 2000,
  prefix = "",
  suffix = "",
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime;
    const numericValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^0-9.-]/g, ""))
        : value;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      setDisplayValue(Math.floor(progress * numericValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(numericValue);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span>
      {prefix}
      {displayValue.toLocaleString()}
      {suffix}
    </span>
  );
};

// Modern stat card component
const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  trend,
  loading,
  delay = 0,
}) => {
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        duration: 0.8,
        delay: delay,
        stiffness: 100,
      },
    },
    hover: {
      y: -4,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      initial={false}
      animate="visible"
      whileHover="hover"
      className="stat-card-container"
    >
      <Card
        className="stat-card"
        sx={{
          background: `linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)`,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
          borderRadius: "20px",
          overflow: "hidden",
          position: "relative",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          transition: "all 0.3s ease",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-40%",
            width: "80%",
            height: "200%",
            background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
            borderRadius: "50%",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-30%",
            left: "-20%",
            width: "50%",
            height: "100%",
            background: `radial-gradient(circle, ${color}10 0%, transparent 60%)`,
            borderRadius: "50%",
            pointerEvents: "none",
          },
          "&:hover": {
            background: `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)`,
            boxShadow: "0 12px 40px 0 rgba(31, 38, 135, 0.12)",
            border: "1px solid rgba(255, 255, 255, 0.7)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent sx={{ p: 4.5, minHeight: '200px', position: 'relative', zIndex: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            height="100%"
          >
            <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between">
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  mb: 3,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.875rem",
                }}
              >
                {title}
              </Typography>

              <Box>
                {loading ? (
                  <Skeleton variant="text" width={120} height={40} />
                ) : (
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 2.5,
                      mt: 1,
                    }}
                  >
                    <AnimatedCounter
                      value={value}
                      prefix={title.includes("Pendapatan") ? "RM " : ""}
                    />
                  </Typography>
                )}

                {trend && (
                  <Box display="flex" alignItems="center" mt={2}>
                    {trend > 0 ? (
                      <TrendingUp
                        sx={{ color: "success.main", fontSize: 20, mr: 0.5 }}
                      />
                    ) : (
                      <TrendingDown
                        sx={{ color: "error.main", fontSize: 20, mr: 0.5 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: trend > 0 ? "success.main" : "error.main",
                        fontWeight: 600,
                      }}
                    >
                      {Math.abs(trend)}% dari bulan lalu
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              className="icon-bubble"
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
                boxShadow: `0 10px 20px ${color}40`,
                position: "relative",
                overflow: "hidden",
                alignSelf: "center",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  width: "200%",
                  height: "200%",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)",
                  animation: "liquid-rotate 10s linear infinite",
                },
              }}
            >
              <Icon sx={{ color: "white", fontSize: 32, zIndex: 1 }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    allMembers: { data: [], value: 0 },
    newMembers: { data: [], value: 0 },
    activeMembers: { data: [], value: 0 },
    revenue: { data: [], value: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const { brandColor, dangerColor, successColor, primaryColor } = useSelector(
    (state) => ({
      brandColor: state.builder?.layoutConfig?.colors?.state?.warning || "#ffb822",
      dangerColor: state.builder?.layoutConfig?.colors?.state?.danger || "#fd3995",
      successColor: state.builder?.layoutConfig?.colors?.state?.success || "#34bfa3",
      primaryColor: state.builder?.layoutConfig?.colors?.state?.primary || "#5867dd",
    }),
    shallowEqual
  );

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const [
        allMembers,
        newMembers,
        activeMembers,
        revenue,
      ] = await Promise.all([
        getAllMembersData(),
        getNewMembersData(),
        getActiveMembersData(),
        getRevenueData(),
      ]);

      setStats({
        allMembers: {
          data: allMembers || [],
          value: allMembers?.[0] || 0,
        },
        newMembers: {
          data: newMembers || [],
          value: newMembers?.[0] || 0,
        },
        activeMembers: {
          data: activeMembers || [],
          value: activeMembers?.[0] || 0,
        },
        revenue: {
          data: revenue || [],
          value: revenue?.[0] || 0,
        },
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const statCards = [
    {
      title: "Jumlah Ahli",
      value: stats.allMembers.value,
      icon: People,
      color: brandColor || "#FFC107",
      trend: 12,
      data: stats.allMembers.data,
    },
    {
      title: "Ahli Baru",
      value: stats.newMembers.value,
      icon: PersonAdd,
      color: primaryColor || "#2196F3",
      trend: 8,
      data: stats.newMembers.data,
    },
    {
      title: "Ahli Aktif",
      value: stats.activeMembers.value,
      icon: CheckCircle,
      color: successColor || "#4CAF50",
      trend: 15,
      data: stats.activeMembers.data,
    },
    {
      title: "Pendapatan Yuran",
      value: stats.revenue.value,
      icon: AttachMoney,
      color: dangerColor || "#F44336",
      trend: -5,
      data: stats.revenue.data,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <DashboardIcon sx={{ fontSize: 32, color: primaryColor }} />
            </Box>

            <Tooltip title="Refresh Data">
              <IconButton
                onClick={fetchAllData}
                disabled={refreshing}
                sx={{
                  background: "rgba(255, 255, 255, 0.9)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    background: "#ffffff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Refresh
                  sx={{
                    color: primaryColor,
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>
      </Box>

      {/* Stats Grid - 2x2 Layout like previous design */}
      <div className="row">
        <div className="col-12">
          <div className="row">
            {/* Left Column */}
            <div className="col-sm-12 col-md-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ marginBottom: "20px" }}
              >
                <StatCard {...statCards[0]} loading={loading} />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <StatCard {...statCards[1]} loading={loading} />
              </motion.div>
            </div>

            {/* Right Column */}
            <div className="col-sm-12 col-md-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                style={{ marginBottom: "20px" }}
              >
                <StatCard {...statCards[2]} loading={loading} />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <StatCard {...statCards[3]} loading={loading} />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
}
